import React, { Component, ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class WebAuthnErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('WebAuthn component error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Biometric Authentication Error
            </Typography>
            <Typography variant="body2" paragraph>
              There was an error loading the biometric authentication settings. 
              This could be due to browser compatibility or network issues.
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
              variant="contained"
              size="small"
            >
              Retry
            </Button>
          </Alert>
          {process.env.NODE_ENV === 'development' && (
            <Alert severity="info">
              <Typography variant="caption">
                Development Error: {this.state.error?.message}
              </Typography>
            </Alert>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default WebAuthnErrorBoundary;